import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import UnauthorizedLayout from '../UnauthorizedLayout'
import FormButton from '../../ui/FormButton'
import FormInput from '../../ui/FormInput'
import AuthService from '../../../services/AuthService'
import useNotification from '../../../hooks/useNotification'
import { resetSeenLevelsOnLogin } from '../../../contexts/OnboardingContext'

const MfaChallenge = ({ mfaToken }) => {
  const { t } = useTranslation('auth')
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    resetField,
  } = useForm()

  const { error: showError } = useNotification()
  const [, setCookie] = useCookies(['access_token_cookie'])
  const navigate = useNavigate()
  const [trustDevice, setTrustDevice] = useState(false)

  // If no mfa_token, show session expired message
  if (!mfaToken) {
    return (
      <UnauthorizedLayout
        title={t('mfa.session_expired')}
        question={t('login.question')}
        text={t('login.signupLink')}
        hancleClick={() => navigate('/login')}
      >
        <div className="w-[100%] desktop:w-[500px] flex flex-col gap-4 items-center mt-6">
          <button
            className="text-white underline hover:opacity-80 transition"
            onClick={() => navigate('/login')}
          >
            {t('login.button')}
          </button>
        </div>
      </UnauthorizedLayout>
    )
  }

  const onSubmit = async (data) => {
    try {
      const response = await AuthService.mfaVerify(
        mfaToken,
        data.code,
        trustDevice
      )
      const access_token = response.data.token

      // Clear the mfa_token from sessionStorage
      sessionStorage.removeItem('mfa_token')

      // Reset onboarding seen levels for fresh tour after MFA
      resetSeenLevelsOnLogin()

      // Set cookie with explicit options
      // Il cookie arriva dalla risposta (Set-Cookie HttpOnly).

      // Navigate to home with cache bust
      const timestamp = Date.now()
      window.location.replace(`/?_t=${timestamp}`)
    } catch (e) {
      console.error('MFA verification failed:', e?.response?.data)
      const errorDetail =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.response?.data?.message
      const errorMessage = errorDetail || t('mfa.invalid_code')
      showError(errorMessage)
      resetField('code')
    }
  }

  const goToLogin = () => {
    sessionStorage.removeItem('mfa_token')
    navigate('/login')
  }

  return (
    <UnauthorizedLayout
      title={t('mfa.challenge_title')}
      question=""
      text=""
      hancleClick={() => {}}
    >
      <div className="w-[100%] desktop:w-[500px] flex flex-col gap-4">
        <p className="text-center text-sm text-white text-opacity-70 font-light">
          {t('mfa.challenge_subtitle')}
        </p>
        <form
          className="mt-2 flex flex-col gap-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormInput
            label={t('mfa.code_label')}
            name="code"
            type="text"
            placeholder={t('mfa.code_placeholder')}
            register={register}
            validationRules={{
              required: t('mfa.code_label'),
            }}
            errors={errors}
          />
          <label className="flex items-center gap-2 text-sm text-white text-opacity-80 font-light cursor-pointer mt-1 mb-2">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              className="w-4 h-4 rounded border-white border-opacity-30 bg-transparent accent-blue-500"
            />
            {t('mfa.trust_device')}
          </label>
          <FormButton
            isSubmitting={isSubmitting}
            text={isSubmitting ? t('mfa.verifying') : t('mfa.verify_button')}
          />
        </form>
        <div className="flex justify-center mt-2">
          <button
            className="text-white text-opacity-70 text-sm hover:text-opacity-100 transition"
            onClick={goToLogin}
            type="button"
          >
            {t('forgotPassword.backLink')}
          </button>
        </div>
      </div>
    </UnauthorizedLayout>
  )
}

export default MfaChallenge
